"use client";

import * as React from "react";
import { createPortal } from "react-dom";

/**
 * Update Multiple for Crew Timesheet only.
 * Applies entered labor-type hours to selected crew employees in the parent grid.
 * Hours persist to Miter when the user clicks Save and Log Production.
 */

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="close-icon"
      style={{
        aspectRatio: "1/1",
        fill: "#424143",
        position: "relative",
        width: "18px",
        height: "18px",
      }}
    >
      <path
        d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
        fill="#424143"
      />
    </svg>
  );
}

const TABLE_COLUMNS =
  "grid w-full min-w-0 grid-cols-[88px_minmax(0,1fr)_110px] sm:min-w-[620px] sm:grid-cols-[1fr_1.2fr_1.4fr]";
const ROW_HEIGHT = "min-h-[56px] sm:h-[65px]";
const PARTIAL_HOURS_INPUT_RE = /^\d*\.?\d*$/;

/** Match Crew Timesheet Non-Project column ids. */
const NON_PROJECT_COLUMN_IDS = new Set(["ware-house", "equip-repair"]);

/** Same band colors as Crew Timesheet Project Labor / Non-Project sub-headers. */
const PROJECT_LABOR_LABEL_BG = "bg-[#EAF4FB]";
const NON_PROJECT_LABEL_BG = "bg-[#FAF0E4]";

const isValidPartialHoursInput = (value) =>
  value === "" || PARTIAL_HOURS_INPUT_RE.test(value);

const normalizeHoursOnBlur = (value) => {
  if (value === "" || value == null) return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const toNumber = (value) => {
  if (value === "" || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const formatAllocatedHours = (value) => {
  const n = round2(toNumber(value));
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const getEmployeeAllocatedHours = (employee, laborColumns = []) => {
  if (!employee?.hours || typeof employee.hours !== "object") return 0;
  if ((laborColumns || []).length > 0) {
    return round2(
      laborColumns.reduce(
        (sum, col) => sum + toNumber(employee.hours?.[col.id]),
        0
      )
    );
  }
  return round2(
    Object.values(employee.hours).reduce((sum, value) => sum + toNumber(value), 0)
  );
};

function EmployeeCheckbox({ name, allocatedHours = 0, checked = false, onChange }) {
  return (
    <label className="flex gap-2 items-center bg-white rounded-sm cursor-pointer">
      <div className="relative h-[18px] w-[18px]">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange?.(e.target.checked)}
          className="absolute top-0 left-0 rounded-sm border border-solid border-zinc-300 h-[18px] w-[18px] cursor-pointer"
        />
      </div>
      <span className="font-poppins text-[14px] font-normal text-[#2C2C2C]">
        {name}{" "}
        <span className="text-[#1E5A8A]">
          ({formatAllocatedHours(allocatedHours)})
        </span>
      </span>
    </label>
  );
}

const getLaborGroup = (columnId) =>
  NON_PROJECT_COLUMN_IDS.has(String(columnId || ""))
    ? "non-project"
    : "project";

const getLaborLabelBg = (group) =>
  group === "non-project" ? NON_PROJECT_LABEL_BG : PROJECT_LABOR_LABEL_BG;

function LaborRow({
  categoryHours,
  categoryLabel,
  totalHours,
  laborGroup = "project",
  onHoursChange,
  onHoursBlur,
}) {
  const labelBg = getLaborLabelBg(laborGroup);
  return (
    <div className={`${TABLE_COLUMNS} ${ROW_HEIGHT} border-b border-gray-200`}>
      <div className="flex items-center justify-center bg-gray-200 border-b border-white">
        <span className="text-sm font-medium text-zinc-800">{categoryHours}</span>
      </div>
      <div className={`flex items-center justify-center ${labelBg} px-2`}>
        <span className="text-sm text-center text-zinc-800">{categoryLabel}</span>
      </div>
      <div className="flex items-center justify-center bg-white px-2 sm:px-6">
        <div className="w-full max-w-[260px] flex items-center justify-center">
          <input
            type="text"
            inputMode="decimal"
            value={totalHours}
            onChange={(e) => onHoursChange?.(e.target.value)}
            onFocus={(e) => e.target.select()}
            onBlur={() => onHoursBlur?.()}
            className="w-full h-[38px] border border-gray-300 rounded text-sm font-semibold text-center outline-none bg-white"
          />
        </div>
      </div>
    </div>
  );
}

function LaborTable({ laborData = [], onHoursChange, onHoursBlur }) {
  return (
    <section className="w-full">
      <div className="overflow-x-auto">
        <div className="border border-gray-300 rounded-md overflow-hidden w-full min-w-0 sm:min-w-[620px]">
          <header
            className={`${TABLE_COLUMNS} ${ROW_HEIGHT} border-b border-gray-300`}
          >
            <div className="flex items-center justify-center bg-gray-200 h-full">
              <h3 className="text-xs sm:text-sm font-semibold text-neutral-700 text-center px-1">
                Total Crew Hours
              </h3>
            </div>
            <div className="flex items-center justify-center bg-gray-200 h-full">
              <h3 className="text-xs sm:text-sm font-semibold text-neutral-700 text-center px-1">
                Labor Type
              </h3>
            </div>
            <div className="flex items-center justify-center bg-gray-200 h-full">
              <h3 className="text-xs sm:text-sm font-semibold text-neutral-700 text-center px-1">
                Total Hours
              </h3>
            </div>
          </header>
          {laborData.map((item, index) => (
            <LaborRow
              key={item.key ?? index}
              categoryHours={item.categoryHours}
              categoryLabel={item.categoryLabel}
              totalHours={item.totalHours}
              laborGroup={item.laborGroup}
              onHoursChange={(value) => onHoursChange?.(index, value)}
              onHoursBlur={() => onHoursBlur?.(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function EmployeeSelection({
  employees = [],
  laborColumns = [],
  selectedIds = {},
  onSelectionChange,
  onToggleAll,
  allSelected = false,
  someSelected = false,
}) {
  const selectAllRef = React.useRef(null);

  React.useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  return (
    <section>
      <h2 className="text-base font-semibold text-neutral-700 mb-4">
        Select Employee
      </h2>
      <label className="mb-4 flex w-fit gap-2 items-center cursor-pointer">
        <input
          ref={selectAllRef}
          type="checkbox"
          checked={allSelected}
          onChange={(e) => onToggleAll?.(e.target.checked)}
          aria-label="Select all employees"
          className="rounded-sm border border-solid border-zinc-300 h-[18px] w-[18px] shrink-0 cursor-pointer"
        />
        <span className="font-poppins text-[14px] font-semibold text-[#2C2C2C]">
          Select all
        </span>
      </label>
      <div className="flex flex-wrap gap-4 content-center items-center max-sm:gap-3">
        {employees.map((employee) => (
          <EmployeeCheckbox
            key={employee.id}
            name={employee.name}
            allocatedHours={getEmployeeAllocatedHours(employee, laborColumns)}
            checked={!!selectedIds[employee.id]}
            onChange={(checked) => onSelectionChange?.(employee.id, checked)}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   employees: Array<{ id: string, name: string, hours?: Record<string, number|string> }>,
 *   laborColumns: Array<{ id: string, label: string }>,
 *   onUpdateHours: (payload: { employeeIds: string[], hoursByColumnId: Record<string, number> }) => void,
 * }} props
 */
function CrewTimesheetUpdateMultipleModal({
  isOpen,
  onClose,
  employees = [],
  laborColumns = [],
  onUpdateHours,
}) {
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState({});
  const [laborData, setLaborData] = React.useState([]);
  const [saveMessage, setSaveMessage] = React.useState(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setSelectedEmployeeIds({});
    setSaveMessage(null);

    const rows = (laborColumns || []).map((col) => {
      const currentTotal = round2(
        (employees || []).reduce(
          (sum, emp) => sum + toNumber(emp?.hours?.[col.id]),
          0
        )
      );
      return {
        key: col.id,
        columnId: col.id,
        categoryHours: currentTotal.toFixed(2),
        categoryLabel: col.label || col.id,
        laborGroup: getLaborGroup(col.id),
        totalHours: 0,
      };
    });

    setLaborData(rows);
  }, [isOpen, employees, laborColumns]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const selectedEmployees = React.useMemo(
    () => (employees || []).filter((emp) => !!selectedEmployeeIds[emp.id]),
    [employees, selectedEmployeeIds]
  );

  const selectedCount = selectedEmployees.length;
  const allSelected =
    employees.length > 0 && selectedCount === employees.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const laborDataWithSelectedTotals = React.useMemo(
    () =>
      laborData.map((row) => {
        const selectedColumnTotal = round2(
          selectedEmployees.reduce(
            (sum, emp) => sum + toNumber(emp?.hours?.[row.columnId || row.key]),
            0
          )
        );
        return {
          ...row,
          categoryHours: selectedColumnTotal.toFixed(2),
        };
      }),
    [laborData, selectedEmployees]
  );

  const handleEmployeeSelectionChange = (id, checked) => {
    setSelectedEmployeeIds((prev) => ({ ...prev, [id]: checked }));
    setSaveMessage(null);
  };

  const handleToggleAll = (checked) => {
    if (!checked) {
      setSelectedEmployeeIds({});
    } else {
      const next = {};
      (employees || []).forEach((emp) => {
        next[emp.id] = true;
      });
      setSelectedEmployeeIds(next);
    }
    setSaveMessage(null);
  };

  const handleHoursChange = (index, value) => {
    if (!isValidPartialHoursInput(value)) return;
    setLaborData((prev) =>
      prev.map((item, i) => (i === index ? { ...item, totalHours: value } : item))
    );
    setSaveMessage(null);
  };

  const handleHoursBlur = (index) => {
    setLaborData((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const normalized = normalizeHoursOnBlur(item.totalHours);
        if (item.totalHours === normalized) return item;
        return { ...item, totalHours: normalized };
      })
    );
  };

  const handleUpdateHours = () => {
    if (selectedCount === 0) {
      setSaveMessage({ type: "error", text: "Select at least one employee." });
      return;
    }
    if ((laborColumns || []).length === 0) {
      setSaveMessage({
        type: "error",
        text: "No labor types available to update.",
      });
      return;
    }

    const hoursByColumnId = {};
    laborData.forEach((row) => {
      const columnId = row.columnId || row.key;
      if (!columnId) return;
      hoursByColumnId[columnId] = toNumber(row.totalHours);
    });

    onUpdateHours?.({
      employeeIds: selectedEmployees.map((e) => String(e.id)),
      hoursByColumnId,
    });

    onClose?.();
  };

  if (!isOpen) return null;

  const canUpdate = selectedCount > 0 && (laborColumns || []).length > 0;

  return createPortal(
    <div className="fixed inset-0 z-[10220] bg-black/50">
      <div className="absolute inset-2 lg:inset-6 flex min-h-0 justify-center">
        <div className="flex h-full max-h-full min-h-0 w-full max-w-[900px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
          <header className="relative z-10 flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-[18px] rounded-t-xl shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
            <h2 className="font-poppins text-[20px] font-bold text-[#2C2C2C]">
              Update Multiple
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-100"
              aria-label="Close modal"
            >
              <CloseIcon />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-4 sm:p-6 [-webkit-overflow-scrolling:touch]">
            {employees.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No employees on this crew timesheet yet. Add employees first.
              </div>
            ) : laborColumns.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No labor types available. Add a labor type first.
              </div>
            ) : (
              <>
                <EmployeeSelection
                  employees={employees}
                  laborColumns={laborColumns}
                  selectedIds={selectedEmployeeIds}
                  onSelectionChange={handleEmployeeSelectionChange}
                  onToggleAll={handleToggleAll}
                  allSelected={allSelected}
                  someSelected={someSelected}
                />
                <div className="mt-6">
                  <LaborTable
                    laborData={laborDataWithSelectedTotals}
                    onHoursChange={handleHoursChange}
                    onHoursBlur={handleHoursBlur}
                  />
                </div>
                <div className="mt-8 mb-2">
                  {saveMessage && (
                    <p
                      className={`text-sm text-center mb-2 ${
                        saveMessage.type === "success"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {saveMessage.text}
                    </p>
                  )}
                  <div className="max-w-[296px] mx-auto">
                    <button
                      type="button"
                      onClick={handleUpdateHours}
                      disabled={!canUpdate}
                      className={`inline-flex justify-center items-center h-[47px] w-full rounded text-white text-lg ${
                        canUpdate
                          ? "bg-[#AD2226] hover:bg-[#8F1B1E] cursor-pointer"
                          : "bg-gray-200 cursor-not-allowed"
                      }`}
                    >
                      Update Hours
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CrewTimesheetUpdateMultipleModal;
